import React, { useState, useEffect } from 'react';
import { 
    Database, Activity, ShieldCheck, RefreshCw, 
    HardDrive, AlertCircle, Clock, Server,
    Layers, Cpu, Globe, Search, ArrowRight,
    Loader2, Trash2, X, AlertTriangle, FileCode,
    Signal, Zap, Microscope, CheckCircle, WifiOff,
    Image as ImageIcon
} from 'lucide-react';
import { StorageService } from '../../services/storageService';
import { PageLoader } from '../../components/PageLoader';
import { MediaAuditManager } from '../../components/admin/MediaAuditManager';

interface DBStats {
    dbName: string;
    collections: {
        name: string;
        count: number;
        size: number;
        storageSize: number;
        indexSize: number;
    }[];
    totalSize: number;
    storageSize: number;
    indexSize: number;
    objectCount: number;
    readyState: number;
    error?: string;
}

export const DatabaseManager: React.FC = () => {
    const [stats, setStats] = useState<DBStats | null>(null);
    const [logs, setLogs] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState<'telemetry' | 'pulse' | 'media-audit'>('telemetry');
    const [status, setStatus] = useState<{type: 'success' | 'error', text: string} | null>(null);

    const loadData = async () => {
        setIsRefreshing(true);
        try {
            const [dbData, logData] = await Promise.all([
                StorageService.getDatabaseStats(),
                StorageService.getSystemLogs()
            ]);
            setStats(dbData);
            setLogs(logData);
            
            if (dbData?.error) {
                setStatus({ type: 'error', text: `Registry Logic Error: ${dbData.error}` });
            } else if (dbData?.readyState !== 1) {
                const stateText = dbData?.readyState === 2 ? 'Initializing' : 'Offline';
                setStatus({ type: 'error', text: `Registry Bridge State: ${stateText}` });
            } else {
                setStatus(null);
            }
        } catch (e) {
            setStatus({ type: 'error', text: 'Registry Bridge Failure: Node unreachable.' });
            setStats(null);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        loadData();
        const interval = setInterval(loadData, 60000); // Auto refresh every minute
        return () => clearInterval(interval);
    }, []);

    const formatSize = (bytes: number) => {
        const val = bytes ?? 0;
        if (val === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(val) / Math.log(k));
        return parseFloat((val / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleClearLogs = async () => {
        if (!confirm('Permanently purge System Pulse archive?')) return;
        try {
            await StorageService.clearSystemLogs();
            setLogs([]);
            setStatus({ type: 'success', text: 'Pulse archive purged.' });
        } catch (e) {}
    };

    const getReadyStateColor = (state: number) => {
        switch(state) {
            case 1: return 'text-emerald-500';
            case 2: return 'text-amber-500';
            default: return 'text-rose-500';
        }
    };

    const getReadyStateText = (state: number) => {
        switch(state) {
            case 1: return 'Operational';
            case 2: return 'Connecting';
            case 3: return 'Disconnecting';
            default: return 'Offline';
        }
    };

    if (isLoading) return <PageLoader message="Establishing Registry Bridge..." />;

    const isOffline = stats?.readyState !== 1;

    return (
        <div className="space-y-8 animate-fade-in pb-24 text-slate-900 max-w-7xl mx-auto px-4 md:px-0">
            
            <div className="flex flex-col lg:flex-row justify-between items-stretch gap-6 bg-slate-950 text-white p-8 rounded-[2.5rem] shadow-2xl relative overflow-hidden group border border-white/5">
                <div className="absolute top-0 right-0 p-8 opacity-5 group-hover:scale-110 transition-transform duration-1000 pointer-events-none">
                    <Database size={240} />
                </div>
                <div className="relative z-10 flex-1">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-3 bg-indigo-600 rounded-2xl shadow-xl">
                            <Activity size={24}/>
                        </div>
                        <h2 className="text-3xl font-black tracking-tight">Database Intelligence</h2>
                    </div>
                    <p className="text-slate-400 font-medium text-sm md:text-lg max-w-2xl">
                        Real-time infrastructure monitoring for the MongoDB Atlas registry.
                    </p>
                    
                    <div className="mt-8 flex gap-3">
                        <button 
                            onClick={loadData}
                            className="bg-white/5 border border-white/10 hover:bg-white/10 px-6 py-3 rounded-xl transition-all flex items-center gap-3"
                        >
                            <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-200">Sync Stats</span>
                        </button>
                    </div>
                </div>

                <div className="w-full lg:w-72 bg-white/5 rounded-3xl p-6 border border-white/10 backdrop-blur-md flex flex-col justify-between">
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <span className="text-[10px] font-black text-slate-500 tracking-widest">Engine Status</span>
                            <div className={`flex items-center gap-1.5 ${getReadyStateColor(stats?.readyState ?? 0)}`}>
                                <div className={`w-2 h-2 rounded-full bg-current ${stats?.readyState === 1 ? 'animate-pulse' : (stats?.readyState === 2 ? 'animate-ping' : '')}`}></div>
                                <span className="text-[9px] font-black">{getReadyStateText(stats?.readyState ?? 0)}</span>
                            </div>
                        </div>
                        <div className="h-px bg-white/10"></div>
                        <div>
                            <p className="text-2xl font-black">{formatSize(stats?.storageSize ?? 0)}</p>
                            <p className="text-[8px] font-black text-slate-500 tracking-widest mt-1">Registry Footprint</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex bg-white p-1 rounded-2xl border border-slate-200 shadow-sm w-fit gap-1">
                <button 
                    onClick={() => setActiveTab('telemetry')}
                    className={`px-8 py-3 rounded-xl text-[10px] font-black tracking-widest flex items-center gap-3 transition-all ${activeTab === 'telemetry' ? 'bg-slate-900 text-white shadow-xl' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <Signal size={16} /> Registry Telemetry
                </button>
                <button 
                    onClick={() => setActiveTab('pulse')}
                    className={`px-8 py-3 rounded-xl text-[10px] font-black tracking-widest flex items-center gap-3 transition-all ${activeTab === 'pulse' ? 'bg-rose-600 text-white shadow-xl' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <Zap size={16} /> System Pulse ({logs.length})
                </button>
                <button 
                    onClick={() => setActiveTab('media-audit')}
                    className={`px-8 py-3 rounded-xl text-[10px] font-black tracking-widest flex items-center gap-3 transition-all ${activeTab === 'media-audit' ? 'bg-indigo-600 text-white shadow-xl' : 'text-slate-400 hover:text-slate-600'}`}
                >
                    <ImageIcon size={16} /> Wix Media CDN & Audit
                </button>
            </div>

            {status && (
                <div className={`p-4 rounded-2xl flex items-center justify-between animate-pop border-2 ${status.type === 'error' ? 'bg-rose-50 border-rose-100 text-rose-700' : 'bg-emerald-50 border-emerald-100 text-emerald-700'}`}>
                    <div className="flex items-center gap-3 ml-2 min-w-0">
                        {status.type === 'error' ? <AlertTriangle size={18} className="shrink-0" /> : <CheckCircle size={18} className="shrink-0" />}
                        <span className="font-black text-xs tracking-widest truncate">{status.text}</span>
                    </div>
                    <button onClick={() => setStatus(null)} className="p-1 hover:bg-black/5 rounded-lg"><X size={16}/></button>
                </div>
            )}

            {activeTab === 'telemetry' && (
                <div className="space-y-8 animate-slide-up">
                    {isOffline ? (
                        <div className="bg-white rounded-[3rem] p-24 text-center border-4 border-dashed border-slate-100 flex flex-col items-center gap-8 grayscale opacity-40">
                             <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center shadow-inner">
                                <WifiOff size={48} className="text-rose-400" />
                            </div>
                            <div className="space-y-2">
                                <p className="text-2xl font-black text-slate-900 tracking-tighter">Connection Restricted</p>
                                <p className="text-sm font-bold text-slate-500 tracking-widest">"Telemetry stream unavailable while engine is {getReadyStateText(stats?.readyState ?? 0).toLowerCase()}."</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <MetricCard icon={Layers} label="Registry Nodes" val={(stats?.objectCount ?? 0).toLocaleString()} color="indigo" />
                                <MetricCard icon={FileCode} label="Index Size" val={formatSize(stats?.indexSize ?? 0)} color="emerald" />
                                <MetricCard icon={HardDrive} label="Logical Data" val={formatSize(stats?.totalSize ?? 0)} color="amber" />
                                <MetricCard icon={Globe} label="Namespace" val={stats?.dbName || '---'} color="medical" />
                            </div>

                            <div className="bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden">
                                <div className="p-8 border-b border-slate-100 flex items-center justify-between">
                                    <h3 className="text-xl font-black tracking-tight flex items-center gap-3">
                                        <Microscope size={24} className="text-indigo-600" /> Registry Inventory
                                    </h3>
                                    <span className="text-[10px] font-black text-slate-400">{stats?.collections?.length ?? 0} Partitions Identified</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="bg-slate-50/50">
                                            <tr>
                                                <th className="py-5 px-8 text-[9px] font-black text-slate-400 tracking-widest">Partition Name</th>
                                                <th className="py-5 px-8 text-[9px] font-black text-slate-400 tracking-widest text-center">Node Count</th>
                                                <th className="py-5 px-8 text-[9px] font-black text-slate-400 tracking-widest text-right">Logical Size</th>
                                                <th className="py-5 px-8 text-[9px] font-black text-slate-400 tracking-widest text-right">Atlas Storage</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-50">
                                            {stats?.collections?.map(col => {
                                                const isScoped = col.name.includes('_');
                                                return (
                                                    <tr key={col.name} className="group hover:bg-slate-50/50 transition-colors">
                                                        <td className="py-4 px-8">
                                                            <div className="flex items-center gap-3">
                                                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isScoped ? 'bg-indigo-50 text-indigo-600' : 'bg-slate-100 text-slate-500'}`}>
                                                                    {isScoped ? <Zap size={14}/> : <Database size={14}/>}
                                                                </div>
                                                                <span className="font-black text-xs tracking-tight text-slate-800">{col.name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="py-4 px-8 text-center">
                                                            <span className="text-xs font-bold tabular-nums text-slate-600">{(col.count ?? 0).toLocaleString()}</span>
                                                        </td>
                                                        <td className="py-4 px-8 text-right">
                                                            <span className="text-xs font-bold text-slate-500">{formatSize(col.size ?? 0)}</span>
                                                        </td>
                                                        <td className="py-4 px-8 text-right">
                                                            <div className="flex flex-col items-end">
                                                                <span className="text-xs font-black text-indigo-600 tabular-nums">{formatSize(col.storageSize ?? 0)}</span>
                                                                <span className="text-[8px] font-bold text-slate-300">Compressed</span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'pulse' && (
                <div className="space-y-8 animate-slide-up">
                    <div className="bg-white rounded-[3rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[700px]">
                        <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-rose-50 text-rose-600 rounded-2xl">
                                    <Signal size={20}/>
                                </div>
                                <div>
                                    <h3 className="text-xl font-black tracking-tight">Logical Event Stream</h3>
                                    <p className="text-[10px] font-black text-slate-400 tracking-widest mt-1">Last 100 System Logs</p>
                                </div>
                            </div>
                            <button 
                                onClick={handleClearLogs}
                                className="px-6 py-3 bg-white text-rose-500 border border-rose-100 rounded-xl font-black text-[10px] tracking-widest hover:bg-rose-50 transition-all flex items-center gap-2 active:scale-95"
                            >
                                <Trash2 size={16}/> Purge Archive
                            </button>
                        </div>
                        
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
                            {logs.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-center opacity-20 grayscale gap-4">
                                    <ShieldCheck size={80} strokeWidth={1} />
                                    <p className="font-black tracking-widest text-sm">System integrity verified.<br/>No events indexed.</p>
                                </div>
                            ) : (
                                logs.map((log, i) => (
                                    <div key={i} className={`p-6 rounded-[2rem] border-2 transition-all group ${log.level === 'error' ? 'bg-rose-50/50 border-rose-100 hover:border-rose-300' : 'bg-slate-50 border-slate-100 hover:border-indigo-300'}`}>
                                        <div className="flex justify-between items-start mb-4">
                                            <div className="flex items-center gap-4">
                                                <div className={`px-2.5 py-1 rounded-lg text-[8px] font-black ${log.level === 'error' ? 'bg-rose-600 text-white' : 'bg-slate-900 text-white'}`}>
                                                    {log.level}
                                                </div>
                                                <span className="text-[10px] font-black text-slate-400 flex items-center gap-1.5">
                                                    <Clock size={12}/> {new Date(log.timestamp).toLocaleString()}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-[8px] font-black text-indigo-500 px-2 py-0.5 bg-white border border-indigo-100 rounded shadow-sm">{log.method}</span>
                                                <span className="font-mono text-[9px] text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-100">{log.path}</span>
                                            </div>
                                        </div>
                                        <h5 className="font-black text-sm text-slate-800 leading-snug mb-3">"{log.message}"</h5>
                                        
                                        {log.stack && (
                                            <div className="mt-4 p-4 bg-slate-950 rounded-2xl border border-white/5 overflow-x-auto max-h-40 custom-scrollbar group-hover:bg-black transition-colors">
                                                <pre className="text-[9px] font-mono text-medical-400 whitespace-pre-wrap leading-relaxed opacity-60 group-hover:opacity-100 transition-opacity">{log.stack}</pre>
                                            </div>
                                        )}
                                        
                                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <span className="text-[8px] font-black text-slate-400">Context Identity:</span>
                                                <span className="text-[9px] font-bold text-slate-600">{log.user}</span>
                                            </div>
                                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                                                <AlertCircle size={12} className="text-rose-400" />
                                                <span className="text-[7px] font-black text-rose-500">Trace Log Validated</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'media-audit' && (
                <div className="animate-slide-up">
                    <MediaAuditManager />
                </div>
            )}
        </div>
    );
};

const MetricCard = ({ icon: Icon, label, val, color }: any) => (
    <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-slate-200 shadow-sm flex items-center gap-6 group hover:border-indigo-500 transition-all">
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110 group-hover:rotate-3 shadow-lg ${
            color === 'indigo' ? 'bg-indigo-600 text-white' :
            color === 'emerald' ? 'bg-emerald-500 text-white' :
            color === 'amber' ? 'bg-amber-500 text-white' :
            'bg-medical-500 text-white'
        }`}>
            <Icon size={28}/>
        </div>
        <div>
            <p className="text-[8px] font-black text-slate-400 tracking-[0.2em] mb-1">{label}</p>
            <p className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">{val}</p>
        </div>
    </div>
);

const ToggleLeft = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor" fillOpacity="0.1"/><circle cx="12" cy="12" r="8" fill="white" shadow-lg="true"/></svg>
);

const ToggleRight = ({size}: {size: number}) => (
    <svg width={size} height={size/2} viewBox="0 0 48 24" fill="none"><rect width="48" height="24" rx="12" fill="currentColor"/><circle cx="36" cy="12" r="8" fill="white"/></svg>
);